import get from 'lodash/get';
import {
  getMetricsV3,
  getMappedV3Data,
  exportMetricsV3 as exportMetricsV3Api
} from 'common/services/metrics.service';
import { getAllContributorsTeamId } from 'common/utils/utils';
import { useSelector } from 'react-redux';
import { teamsSelector } from 'state/selectors/teams';
import { SETTING_DEFINITION_LEVELS } from 'common/constants/constants';
import { GROUP_BY_KEY } from '../consts';

const useMetricsV3Data = () => {
  const teams = useSelector(teamsSelector);
  const allContributorsTeamId = getAllContributorsTeamId(teams);
  let teamId;

  const search = async options => {
    if (!(options?.requested_metrics ?? options?.custom_metrics ?? []).length) {
      return [];
    }

    const { team_ids } = options;

    if (team_ids?.length === 1) {
      teamId = team_ids[0];
    }

    // when all contributors selected
    if (allContributorsTeamId === teamId) {
      return await searchForAllContributors(options);
    }

    return await getMetricsV3(options);
  };

  const searchForAllContributors = async options => {
    const orgOptions = {
      team_ids: [],
      group_by: SETTING_DEFINITION_LEVELS.organization,
      requested_metrics: options.requested_metrics,
      roll_up: options.roll_up,
      time_ranges: options.time_ranges
    };
    const results = await getMetricsV3(orgOptions);
    return results;
  };

  const exportMetricsV3 = async options => {
    if (!(options?.requested_metrics ?? options?.custom_metrics ?? []).length) {
      return [];
    }
    const results = await exportMetricsV3Api(options);
    return results;
  };
  const fetchNewV3MetricsDataByFilter = async ({
    metricsOptions,
    averageOptions,
    isTotalsNeeded,
    filteredByLabel
  }) => {
    const filteredByTeams = metricsOptions?.team_ids?.length >= 1;

    const averageOptionsPerGroup = {
      ...averageOptions,
      group_by: filteredByLabel ? GROUP_BY_KEY.LABEL : GROUP_BY_KEY.TEAM
    };

    const promiseSet = [search(metricsOptions)];

    // check if only for old all teams = metricsOptions?.team_ids?.length === 0
    if (isTotalsNeeded && !filteredByTeams) {
      promiseSet.push(search(averageOptions));
    } else {
      promiseSet.push(Promise.resolve());
    }

    // if multiple teams/labels selected - call search with group_by: 'team'/'label' for totals per team
    if (filteredByTeams || filteredByLabel) {
      promiseSet.push(search(averageOptionsPerGroup));
    } else {
      promiseSet.push(Promise.resolve());
    }

    // special handling for labels - call search with exclude_labels
    if (filteredByLabel) {
      const { labels = [], ...metricsOptionsRest } = metricsOptions;
      const { labels: labelsProp, ...averageOptionsRest } = averageOptions;

      const metricsOptionsExcludeLabels = {
        exclude_labels: labels,
        ...metricsOptionsRest,
        group_by: 'organization'
      };
      const averageOptionsExcludeLabels = {
        exclude_labels: labels,
        ...averageOptionsRest,
        group_by: 'organization'
      };

      promiseSet.push(search(metricsOptionsExcludeLabels));
      promiseSet.push(search(averageOptionsExcludeLabels));
    }

    const promiseSetResult = await Promise.all(promiseSet);

    const metricsData = get(promiseSetResult, '[0]', []);
    const excludedLabelsData = get(promiseSetResult, '[3]', []);
    const metricsTotals = get(promiseSetResult, '[2]', []);
    const excludedLabelsTotals = get(promiseSetResult, '[4]', []);

    // if filter by labels - return metrics data + excluded data (and name it 'baseline')
    const promiseSetResultData = filteredByLabel
      ? metricsData.concat(
          excludedLabelsData.map(result => {
            return { ...result, group: 'baseline' };
          })
        )
      : metricsData;

    // if filter by labels - return metrics totals + excluded totals (and name it 'baseline')
    const promiseSetResultTotalsData = filteredByLabel
      ? metricsTotals.concat(
          excludedLabelsTotals.map(result => {
            return { ...result, group: 'baseline' };
          })
        )
      : metricsTotals;

    return {
      data: promiseSetResultData.map((item, index) => {
        const total = get(promiseSetResultTotalsData, `[${index}].data[0]`, {});
        return {
          ...item,
          ...(filteredByTeams || filteredByLabel
            ? { total: getMappedV3Data({ v3DataResult: total }) }
            : {})
        };
      }),
      totals:
        filteredByTeams && metricsOptions?.team_ids?.length === 1
          ? get(promiseSetResult, '[2][0].data[0]', [])
          : get(promiseSetResult, '[1][0].data[0]', [])
    };
  };

  return {
    search,
    fetchNewV3MetricsDataByFilter,
    exportMetricsV3
  };
};

export default useMetricsV3Data;
